import {
  IsBoolean,
  IsIn,
  IsOptional,
  IsString,
  Matches,
  MaxLength,
  MinLength,
} from 'class-validator';

/**
 * Document review, sign-off and e-signature.
 *
 * An officer submits a published (PDF) generated document for review. A
 * reviewer who holds signing authority for that kind at the file's
 * municipality approves and signs it, requests changes, or rejects it. Signing
 * stamps a copy of the PDF and saves it as the document's next version; the
 * unsigned version is never altered.
 *
 * Shared by the Government Portal web app, the mobile app and gov-api.
 */

export const SIGNOFF_STATUSES = [
  'pending',
  'approved',
  'changes_requested',
  'rejected',
  'withdrawn',
] as const;
export type SignoffStatus = (typeof SIGNOFF_STATUSES)[number];

export const SIGNATURE_METHODS = ['drawn', 'typed', 'stored'] as const;
export type SignatureMethod = (typeof SIGNATURE_METHODS)[number];

export const SIGNOFF_DECISIONS = ['approve', 'request_changes', 'reject'] as const;
export type SignoffDecision = (typeof SIGNOFF_DECISIONS)[number];

export const SIGNOFF_NOTE_MAX = 2000;
export const SIGNOFF_TYPED_NAME_MAX = 120;
/** A drawn signature PNG as a data URL; 400 KB is far beyond a real one. */
export const SIGNATURE_IMAGE_MAX_CHARS = 400_000;
export const SIGNATURE_DATA_URL_PREFIX = 'data:image/png;base64,';

/** One review request, as the file, the queue and the decision screen show it. */
export interface DocumentSignoffView {
  id: string;
  fileId: string;
  fileReference: string;
  localGovernmentId: string;
  formId: string;
  formKind: string;
  formVersion: number;
  status: SignoffStatus;
  submittedById: string | null;
  submittedByName: string;
  submitNote: string | null;
  submittedAt: string;
  reviewerName: string | null;
  reviewerTitle: string | null;
  decisionComment: string | null;
  decidedAt: string | null;
  signatureMethod: SignatureMethod | null;
  signedFormId: string | null;
  signedSha256: string | null;
  verificationRef: string | null;
  signedAt: string | null;
  /** Signed link to the unsigned PDF under review. */
  documentUrl: string | null;
  /** Signed link to the signed PDF, once there is one. */
  signedUrl: string | null;
  /** True when the caller may decide this request (authority, not their own). */
  canDecide: boolean;
  /** True when the caller submitted it. */
  isMine: boolean;
}

/** `GET /files/:fileId/signoffs`. */
export interface FileSignoffs {
  /** The municipality has sign-off switched on. */
  enabled: boolean;
  /** Only signed notices may be recorded as served. */
  required: boolean;
  items: DocumentSignoffView[];
}

/** `GET /signoffs/queue`. */
export interface SignoffQueue {
  /** Sign-off is on at one or more of the caller's municipalities. */
  enabled: boolean;
  pending: DocumentSignoffView[];
  /** The caller's own submissions still open or recently decided. */
  mine: DocumentSignoffView[];
}

/** `POST /files/:fileId/forms/:formId/signoff`. */
export class SubmitSignoffDto {
  @IsOptional()
  @IsString()
  @MaxLength(SIGNOFF_NOTE_MAX)
  note?: string;
}

/**
 * `POST /files/:fileId/signoffs/:id/decision`.
 *
 * Approving signs, so it needs the reviewer's password again (re-auth) and a
 * signature: a drawn PNG, a typed name, or the stored profile image. Requesting
 * changes and rejecting need a comment, which is what the officer acts on.
 */
export class DecideSignoffDto {
  @IsIn(SIGNOFF_DECISIONS)
  decision!: SignoffDecision;

  @IsOptional()
  @IsString()
  @MaxLength(SIGNOFF_NOTE_MAX)
  comment?: string;

  @IsOptional()
  @IsIn(SIGNATURE_METHODS)
  method?: SignatureMethod;

  @IsOptional()
  @IsString()
  @MaxLength(SIGNATURE_IMAGE_MAX_CHARS)
  imageDataUrl?: string;

  @IsOptional()
  @IsString()
  @MaxLength(SIGNOFF_TYPED_NAME_MAX)
  typedName?: string;

  /** Also keep a drawn signature on the reviewer's profile. */
  @IsOptional()
  @IsBoolean()
  saveSignature?: boolean;

  /** Re-authentication: required to approve. */
  @IsOptional()
  @IsString()
  @MaxLength(200)
  password?: string;
}

/** `GET /me/signature`. */
export interface StoredSignature {
  hasSignature: boolean;
  /** Signed link to the image, when there is one. */
  imageUrl: string | null;
  updatedAt: string | null;
}

/** `PUT /me/signature`. */
export class SaveSignatureDto {
  @IsString()
  @MinLength(SIGNATURE_DATA_URL_PREFIX.length + 1)
  @MaxLength(SIGNATURE_IMAGE_MAX_CHARS)
  imageDataUrl!: string;
}

/** `POST /public/signed-documents/verify`. */
export class VerifySignedDocumentDto {
  @IsString()
  @Matches(/^[A-Z0-9-]{6,24}$/i)
  reference!: string;

  /** SHA-256 (hex) of the copy in hand, to confirm it is the signed document. */
  @IsOptional()
  @Matches(/^[a-f0-9]{64}$/i)
  sha256?: string;
}

/**
 * The answer to a verification. Deliberately thin: enough to confirm the paper
 * in front of you, nothing that identifies the property or its owner.
 */
export interface SignedDocumentVerification {
  valid: boolean;
  reference: string;
  formKind?: string;
  signerName?: string;
  signerTitle?: string | null;
  signedAt?: string;
  localGovernmentName?: string;
  sha256?: string;
  /** Present when a hash was sent: whether it matches the signed document. */
  hashMatches?: boolean;
}

/** True when an approve request carries what signing needs. Shared by web, mobile and API. */
export function signatureProblem(dto: {
  method?: SignatureMethod;
  imageDataUrl?: string;
  typedName?: string;
  password?: string;
}): 'password' | 'method' | 'image' | 'typedName' | null {
  if (!dto.password) return 'password';
  if (!dto.method) return 'method';
  if (dto.method === 'drawn') {
    if (!dto.imageDataUrl?.startsWith(SIGNATURE_DATA_URL_PREFIX)) return 'image';
  }
  if (dto.method === 'typed' && !dto.typedName?.trim()) return 'typedName';
  return null;
}
